/**
 * TelemetryReporter — accumulates telemetry spans and flushes them
 * to the AgentVault backend via HTTP POST.
 *
 * Telemetry is best-effort: flush() never throws. On a network error, a 5xx,
 * 408 or 429 the spans stay in the buffer for retry on the next flush cycle —
 * up to `maxRetries` attempts per span, with the buffer capped at
 * `maxBufferedSpans` (oldest dropped first). Any other 4xx means the server
 * rejected the batch itself (bad hub id, invalid span, wrong agent, revoked
 * credential): re-sending the same bytes cannot succeed, so it is dropped.
 *
 * Usage:
 *   const reporter = new TelemetryReporter({
 *     apiBase: "https://api.agentvault.chat",
 *     hubId: "...",
 *     authHeader: "Bearer <jwt>",
 *   });
 *   reporter.reportLlmCall({ model: "gpt-4o", latencyMs: 1200, tokensInput: 500, tokensOutput: 150 });
 *   reporter.startAutoFlush(); // every 30 s
 */

import {
  buildLlmSpan,
  buildToolSpan,
  buildErrorSpan,
  buildHttpSpan,
  buildActionSpan,
  buildNavSpan,
  buildEvalSpan,
  buildTaskSpan,
  buildTraceparent,
  type TelemetrySpan,
  type BuildLlmSpanOpts,
  type BuildToolSpanOpts,
  type BuildErrorSpanOpts,
  type BuildHttpSpanOpts,
  type BuildActionSpanOpts,
  type BuildNavSpanOpts,
  type BuildEvalSpanOpts,
  type BuildTaskSpanOpts,
} from "./telemetry.js";

// -- Config -------------------------------------------------------------------

export interface TelemetryReporterConfig {
  /** Backend base URL, e.g. "https://api.agentvault.chat" */
  apiBase: string;
  /** Agent's hub identity ID */
  hubId: string;
  /** Authorization header value, e.g. "Bearer <jwt>" or API key */
  authHeader: string;
  /** Injectable fetch for testing (defaults to globalThis.fetch) */
  fetchImpl?: typeof fetch;
  /** Attempts per span on retryable failures before it is dropped (default 5). */
  maxRetries?: number;
  /** Max spans held for retry; oldest are dropped beyond this (default 1000). */
  maxBufferedSpans?: number;
  /** Agent display name (resource attribute) */
  agentName?: string;
  /** Agent version (resource attribute) — semantic version of the agent persona */
  agentVersion?: string;
  /**
   * Plugin (npm package) version that's emitting telemetry, e.g. "0.21.5".
   * Surfaces as both `av.plugin.version` (internal namespace) and
   * `service.version` (OTel-canonical) on every span's resource. Lets
   * dashboards answer "which version is loaded on hub X?" via SQL instead
   * of `ps + bundle mtime` detective work.
   */
  pluginVersion?: string;
}

// -- OTLP serialization helpers -----------------------------------------------

interface OtlpAttribute {
  key: string;
  value:
    | { stringValue: string }
    | { intValue: number }
    | { doubleValue: number }
    | { boolValue: boolean };
}

/**
 * Convert a flat attribute record to OTLP KV format.
 *
 * - string  -> { stringValue }
 * - boolean -> { boolValue }
 * - integer -> { intValue }
 * - float   -> { doubleValue }
 */
function toOtlpAttributes(
  attrs: Record<string, string | number | boolean>,
): OtlpAttribute[] {
  return Object.entries(attrs).map(([key, val]) => {
    if (typeof val === "string") {
      return { key, value: { stringValue: val } };
    }
    if (typeof val === "boolean") {
      return { key, value: { boolValue: val } };
    }
    // number: integer vs float
    if (Number.isInteger(val)) {
      return { key, value: { intValue: val as number } };
    }
    return { key, value: { doubleValue: val as number } };
  });
}

/**
 * Convert a TelemetrySpan to the OTLP wire format expected by the
 * backend ingest endpoint.
 *
 * Includes W3C TraceContext attributes (`w3c.traceparent`, `w3c.tracestate`)
 * for cross-agent/cross-sandbox trace correlation per the W3C Trace Context spec.
 */
function spanToOtlp(span: TelemetrySpan): Record<string, unknown> {
  // Inject W3C TraceContext as span attributes for cross-agent correlation
  const enrichedAttrs = { ...span.attributes };
  enrichedAttrs["w3c.traceparent"] = buildTraceparent(span);
  enrichedAttrs["w3c.tracestate"] = `av=s:${span.spanId}`;

  const otlp: Record<string, unknown> = {
    traceId: span.traceId,
    spanId: span.spanId,
    name: span.name,
    kind: span.kind,
    startTimeUnixNano: String(span.startTime * 1_000_000),
    endTimeUnixNano: String(span.endTime * 1_000_000),
    attributes: toOtlpAttributes(enrichedAttrs),
  };

  if (span.parentSpanId !== undefined) {
    otlp.parentSpanId = span.parentSpanId;
  }

  if (span.status) {
    otlp.status = span.status;
  }

  if (span.events && span.events.length > 0) {
    otlp.events = span.events;
  }

  return otlp;
}

// -- TelemetryReporter --------------------------------------------------------

/** Network-level, overload and server failures are worth retrying; other 4xx are not. */
function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

export class TelemetryReporter {
  private readonly _apiBase: string;
  private readonly _hubId: string;
  private readonly _authHeader: string;
  private readonly _fetch: typeof fetch;
  private readonly _agentName: string;
  private readonly _agentVersion: string;
  private readonly _pluginVersion: string;
  private readonly _maxRetries: number;
  private readonly _maxBufferedSpans: number;
  private _buffer: TelemetrySpan[] = [];
  /** Failed send attempts per buffered span. */
  private _attempts = new WeakMap<TelemetrySpan, number>();
  private _timer: ReturnType<typeof setInterval> | null = null;

  constructor(config: TelemetryReporterConfig) {
    this._apiBase = config.apiBase.replace(/\/+$/, ""); // strip trailing slash
    this._hubId = config.hubId;
    this._authHeader = config.authHeader;
    this._fetch = config.fetchImpl ?? globalThis.fetch;
    this._agentName = config.agentName ?? "unknown";
    this._agentVersion = config.agentVersion ?? "0.0.0";
    this._pluginVersion = config.pluginVersion ?? "0.0.0";
    this._maxRetries = Math.max(1, config.maxRetries ?? 5);
    this._maxBufferedSpans = Math.max(1, config.maxBufferedSpans ?? 1000);
  }

  /** Number of spans waiting to be flushed. */
  get pendingCount(): number {
    return this._buffer.length;
  }

  // -- Report methods ---------------------------------------------------------

  /** Record an LLM inference call. */
  reportLlmCall(opts: BuildLlmSpanOpts): void {
    this._buffer.push(buildLlmSpan(opts));
  }

  /** Record a tool/function invocation. */
  reportToolCall(opts: BuildToolSpanOpts): void {
    this._buffer.push(buildToolSpan(opts));
  }

  /** Record an error event. */
  reportError(opts: BuildErrorSpanOpts): void {
    this._buffer.push(buildErrorSpan(opts));
  }

  /** Record an HTTP request call. */
  reportHttpCall(opts: BuildHttpSpanOpts): void {
    this._buffer.push(buildHttpSpan(opts));
  }

  /** Record an action execution. */
  reportActionCall(opts: BuildActionSpanOpts): void {
    this._buffer.push(buildActionSpan(opts));
  }

  /** Record a navigation event. */
  reportNavCall(opts: BuildNavSpanOpts): void {
    this._buffer.push(buildNavSpan(opts));
  }

  /** Record an evaluation event. */
  reportEvaluation(opts: BuildEvalSpanOpts): void {
    this._buffer.push(buildEvalSpan(opts));
  }

  /** Record a task lifecycle event. */
  reportTaskCall(opts: BuildTaskSpanOpts): void {
    this._buffer.push(buildTaskSpan(opts));
  }

  /** Record an arbitrary pre-built span. */
  reportCustomSpan(span: TelemetrySpan): void {
    this._buffer.push(span);
  }

  // -- Flush ------------------------------------------------------------------

  /**
   * POST all buffered spans to the backend ingest endpoint.
   *
   * - On success (HTTP 2xx): clears the buffer.
   * - On a retryable failure (network, 5xx, 408, 429): keeps spans for retry,
   *   bounded by `maxRetries` per span and `maxBufferedSpans` overall.
   * - On any other 4xx: drops the batch (it can never be accepted).
   * - Never throws — telemetry is best-effort.
   */
  async flush(): Promise<void> {
    if (this._buffer.length === 0) {
      return;
    }

    // Snapshot the current buffer — if new spans arrive during the POST
    // they will go into a fresh array.
    const spans = this._buffer;
    this._buffer = [];

    try {
      const response = await this._fetch(
        `${this._apiBase}/api/v1/telemetry/ingest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: this._authHeader,
          },
          body: JSON.stringify({
            hub_id: this._hubId,
            resource: {
              "gen_ai.agent.name": this._agentName,
              "gen_ai.agent.id": this._hubId,
              "gen_ai.agent.version": this._agentVersion,
              "service.name": "agentvault-agent",
              "service.version": this._pluginVersion,
              "av.plugin.version": this._pluginVersion,
            },
            spans: spans.map(spanToOtlp),
          }),
        },
      );

      if (!response.ok && isRetryableStatus(response.status)) {
        this._requeue(spans);
      }
      // 2xx: delivered. Non-retryable 4xx: dropped — never re-sent.
    } catch {
      // Network error — retryable.
      this._requeue(spans);
    }
  }

  /** Put a failed batch back in front of newer spans, within both caps. */
  private _requeue(spans: TelemetrySpan[]): void {
    const retained = spans.filter((span) => {
      const attempts = (this._attempts.get(span) ?? 0) + 1;
      this._attempts.set(span, attempts);
      return attempts < this._maxRetries;
    });
    const merged = retained.concat(this._buffer);
    this._buffer =
      merged.length > this._maxBufferedSpans
        ? merged.slice(merged.length - this._maxBufferedSpans)
        : merged;
  }

  // -- Auto-flush -------------------------------------------------------------

  /**
   * Start a periodic flush timer.
   * @param intervalMs  Flush interval in milliseconds (default 30 000).
   */
  startAutoFlush(intervalMs = 30_000): void {
    this.stopAutoFlush();
    this._timer = setInterval(() => {
      void this.flush();
    }, intervalMs);
  }

  /** Stop the periodic flush timer. Safe to call when not started. */
  stopAutoFlush(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }
}
